import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { WarningAmberRounded } from "@mui/icons-material";
import { V2_COLORS } from "../constants/colors";
import { convertToINR } from "@/helpers/Utils";
import type { PoPaymentEntry } from "../api/invoiceQueries";

/**
 * Payment type tag color mapping. Keys are lower-cased payment_type values:
 * legacy CSV rows send "Invoice" / "Advance", v2 UTR lines (ENG-27224) send
 * "ADVANCE" / "RUNNING" / "FINAL".
 */
const PAYMENT_TYPE_STYLES: Record<string, { color: string; bg: string; border: string }> = {
  invoice: { color: "#3E4690", bg: "#EDEEF7", border: "#DCDEF0" },
  running: { color: "#3E4690", bg: "#EDEEF7", border: "#DCDEF0" },
  final: { color: "#3E4690", bg: "#EDEEF7", border: "#DCDEF0" },
  advance: { color: V2_COLORS.warning, bg: "#FFF8E1", border: V2_COLORS.warning },
  completed: { color: V2_COLORS.successSoft, bg: V2_COLORS.successSoftBg, border: V2_COLORS.successBg },
};

const DEFAULT_TYPE_STYLE = { color: V2_COLORS.textSecondary, bg: V2_COLORS.bgMuted, border: "#CCC" };

/** Payments shown before the "Show N more" toggle. One invoice normally has 1–3. */
export const VISIBLE_PAYMENTS_LIMIT = 5;

// --- Styles (match LineItemsCard / ThreeWayMatchCard so the column reads as one system) ---

const thStyle = {
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "16px",
  color: V2_COLORS.textSecondary,
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap" as const,
  bgcolor: "#F2F2F2",
  borderBottomWidth: 2,
  borderColor: "#E6E6E6",
  py: 1,
  px: 3,
};

const tdStyle = {
  fontSize: "14px",
  lineHeight: "20px",
  py: 1.5,
  px: 3,
  borderColor: "#E6E6E6",
};

const CARD_SX = { bgcolor: "background.paper", borderRadius: "8px", py: 2, pl: 1.5, pr: 3 };
const CARD_SX_MOBILE = { bgcolor: "background.paper", borderRadius: "8px", p: 1.5 };

interface PaymentSummaryCardProps {
  /** UTR lines for this invoice, filtered from PO details `payments[]`. */
  payments: PoPaymentEntry[] | undefined;
  /** Cash paid against the invoice (PO details invoice `amount_paid`). */
  cashPaid?: number | null;
  /** PO advance the invoice consumed (PO details invoice `advance_adjusted`). */
  advanceAdjusted?: number | null;
  /**
   * Held credit (open advance) set off against the invoice — PO details invoice
   * `open_advance_adjusted` (ENG-27825 / ENG-27799).
   */
  openAdvanceAdjusted?: number | null;
  /**
   * The invoice's value (PO details invoice `invoice_value`). Decides whether
   * the advance and wallet credit settled it in full or left a balance due —
   * without it the card claims neither.
   */
  invoiceValue?: number | null;
  /**
   * The PO's advance has not been paid to the vendor yet (ENG-28382). The
   * invoice still consumed it, so it is settled on paper, but the vendor has
   * not received that part: the card says so instead of "no UTR was needed".
   */
  advancePending?: boolean;
  isLoading?: boolean;
  isMobile?: boolean;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/** Same rupee formatting as the sibling cards (₹1,234.50, always two decimals). */
function formatAmount(amount: number | null | undefined): string {
  return convertToINR(amount ?? 0);
}

function TypeChip({ paymentType }: { paymentType?: string }) {
  const type = paymentType || "payment";
  const typeStyle = PAYMENT_TYPE_STYLES[type.toLowerCase()] ?? DEFAULT_TYPE_STYLE;
  return (
    <Chip
      label={type.toUpperCase()}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "10px",
        color: typeStyle.color,
        backgroundColor: typeStyle.bg,
        border: `1px solid ${typeStyle.border}`,
      }}
    />
  );
}

/** Desktop: one table row per payment, same header/cell styling as the sibling cards. */
function PaymentsTable({ lines }: { lines: PoPaymentEntry[] }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={thStyle}>Amount</TableCell>
            <TableCell sx={thStyle}>Payment Date</TableCell>
            <TableCell sx={thStyle}>UTR</TableCell>
            <TableCell sx={thStyle}>Type</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((payment, idx) => (
            // Lines have no stable id (v2 lines are rendered into the legacy
            // shape with id 0), so key on position + UTR.
            <TableRow
              key={`${idx}-${payment.utr ?? "no-utr"}`}
              data-testid="payment-entry"
              sx={{ "&:last-child td": { borderBottom: 0 } }}
            >
              <TableCell sx={tdStyle}>{formatAmount(payment.amount)}</TableCell>
              <TableCell sx={tdStyle}>{formatDate(payment.payment_date)}</TableCell>
              <TableCell sx={{ ...tdStyle, overflowWrap: "anywhere" }}>{payment.utr || "—"}</TableCell>
              <TableCell sx={tdStyle}>
                <TypeChip paymentType={payment.payment_type} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box minWidth={0}>
      <Typography variant="caption" component="div" sx={{ color: V2_COLORS.textSecondary }}>
        {label}
      </Typography>
      <Typography variant="body1" component="div" sx={{ overflowWrap: "anywhere" }}>
        {value}
      </Typography>
    </Box>
  );
}

/** Mobile: one 2×2 block per payment — chip + amount, then date + UTR. */
function PaymentsStack({ lines }: { lines: PoPaymentEntry[] }) {
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {lines.map((payment, idx) => (
        <Box
          key={`${idx}-${payment.utr ?? "no-utr"}`}
          data-testid="payment-entry"
          sx={{
            border: `1px solid ${V2_COLORS.border}`,
            borderRadius: "8px",
            p: 1.5,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            rowGap: 1.5,
            columnGap: 2,
          }}
        >
          <Box display="flex" alignItems="center">
            <TypeChip paymentType={payment.payment_type} />
          </Box>
          <Field label="Amount" value={formatAmount(payment.amount)} />
          <Field label="Payment Date" value={formatDate(payment.payment_date)} />
          <Field label="UTR" value={payment.utr || "—"} />
        </Box>
      ))}
    </Box>
  );
}

/** What settled the invoice when no cash moved at all. */
function noCashNote(advance: number, walletCredit: number): string {
  if (advance > 0 && walletCredit > 0) {
    return "Fully settled from the PO advance and wallet credit — no UTR was needed for this invoice.";
  }
  if (walletCredit > 0) return "Fully covered by wallet credit — no UTR was needed for this invoice.";
  return "Fully paid from the PO advance — no UTR was needed for this invoice.";
}

/**
 * Settlement split shown when part of the invoice was covered without cash —
 * by the PO advance and/or held wallet credit (open advance). No money moved
 * for those parts, so they have no UTR line of their own. Laid out like the
 * Line items subtotal footer.
 *
 * `due` is what the invoice still needs once cash, advance and credit are
 * counted: the "no UTR was needed" note shows only when it is 0, and a
 * positive balance gets its own "Still due" line instead. Null (invoice value
 * unknown) shows neither.
 *
 * `advancePending` (the PO advance is not paid yet) replaces any "no UTR was
 * needed" note with one saying the advance is still to be paid, whatever was
 * paid in cash: the vendor has not received that part yet.
 */
function Settlement({
  cashPaid,
  advanceAdjusted,
  walletCredit,
  due,
  advancePending,
  isMobile,
}: {
  cashPaid: number;
  advanceAdjusted: number;
  walletCredit: number;
  due: number | null;
  advancePending: boolean;
  isMobile: boolean;
}) {
  const summary: Array<{ label: string; value: number; unpaid?: boolean }> = [];
  if (cashPaid > 0) summary.push({ label: "Paid via UTR", value: cashPaid });
  if (advanceAdjusted > 0) {
    summary.push(
      advancePending
        ? { label: "From advance", value: advanceAdjusted, unpaid: true }
        : { label: "Paid from advance", value: advanceAdjusted },
    );
  }
  if (walletCredit > 0) summary.push({ label: "Covered by wallet credit", value: walletCredit });
  if (due !== null && due > 0) summary.push({ label: "Still due", value: due });

  return (
    <Box px={isMobile ? 0.5 : 3} pt={isMobile ? 1.5 : 2}>
      {advancePending && (
        <Box
          data-testid="advance-pending-notice"
          display="flex"
          alignItems="center"
          gap={1}
          mb={1.5}
          px={1.5}
          py={1}
          sx={{ bgcolor: V2_COLORS.warningBg, borderRadius: "4px" }}
        >
          <WarningAmberRounded sx={{ fontSize: 16, color: V2_COLORS.warning }} />
          <Typography fontSize="12px">The PO advance used here has not been paid to the vendor yet.</Typography>
        </Box>
      )}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="flex-end"
        alignItems={isMobile ? "stretch" : "center"}
        flexWrap="wrap"
        gap={isMobile ? 0.5 : 3}
      >
        {!advancePending && cashPaid <= 0 && due === 0 && (
          <Typography fontSize="12px" color="textSecondary" sx={{ mr: "auto" }}>
            {noCashNote(advanceAdjusted, walletCredit)}
          </Typography>
        )}
        {summary.map(({ label, value, unpaid }) => (
          <Box
            key={label}
            display="flex"
            alignItems="center"
            gap={1}
            justifyContent={isMobile ? "space-between" : "flex-start"}
          >
            <Typography fontSize="14px" fontWeight={500} color={V2_COLORS.textSecondary}>
              {label}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontSize="14px">{formatAmount(value)}</Typography>
              {unpaid && (
                <Chip
                  label="NOT PAID"
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: "10px",
                    color: V2_COLORS.warning,
                    backgroundColor: V2_COLORS.warningBg,
                    border: `1px solid ${V2_COLORS.warning}`,
                  }}
                />
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function PaymentSummaryCard({
  payments,
  cashPaid,
  advanceAdjusted,
  openAdvanceAdjusted,
  invoiceValue,
  advancePending = false,
  isLoading,
  isMobile = false,
}: PaymentSummaryCardProps) {
  const [showAll, setShowAll] = useState(false);
  const lines = payments ?? [];
  const advance = advanceAdjusted ?? 0;
  const walletCredit = openAdvanceAdjusted ?? 0;
  // Prefer the backend's per-invoice figure; fall back to summing the lines.
  const cash = cashPaid ?? lines.reduce((sum, p) => sum + (p.amount ?? 0), 0);
  // Rounded to the paisa so float noise never reads as a balance due.
  const due =
    invoiceValue == null
      ? null
      : Math.max(0, Math.round((invoiceValue - cash - advance - walletCredit) * 100) / 100);
  const hasLines = lines.length > 0;
  const hasAdvance = advance > 0;
  const hasWalletCredit = walletCredit > 0;
  const hiddenCount = Math.max(0, lines.length - VISIBLE_PAYMENTS_LIMIT);
  const visibleLines = showAll ? lines : lines.slice(0, VISIBLE_PAYMENTS_LIMIT);

  return (
    <Box>
      <Typography variant="h4" mb={1.5}>
        Payment Summary
      </Typography>
      <Box sx={isMobile ? CARD_SX_MOBILE : CARD_SX}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : !hasLines && !hasAdvance && !hasWalletCredit ? (
          <Typography fontSize="14px" color="textSecondary" py={2} textAlign="center">
            No payment records found
          </Typography>
        ) : (
          <>
            {hasLines && (isMobile ? <PaymentsStack lines={visibleLines} /> : <PaymentsTable lines={visibleLines} />)}
            {hiddenCount > 0 && (
              <Button
                size="small"
                onClick={() => setShowAll((v) => !v)}
                sx={{ mt: 1, ml: isMobile ? 0 : 2, textTransform: "none", px: 0.5 }}
              >
                {showAll
                  ? "Show fewer payments"
                  : `Show ${hiddenCount} more payment${hiddenCount > 1 ? "s" : ""}`}
              </Button>
            )}
            {(hasAdvance || hasWalletCredit) && (
              <Settlement
                cashPaid={cash}
                advanceAdjusted={advance}
                walletCredit={walletCredit}
                due={due}
                advancePending={advancePending && hasAdvance}
                isMobile={isMobile}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
